import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
import { ProgressPercentage, ProgressPercentageDTO } from "./progressPercentage";
/* eslint-disable import/export */
export abstract class SubmitSurveyRPC implements
  LeoRPC<SubmitSurveyRPC.Request, SubmitSurveyRPC.Response, SubmitSurveyRPC.Errors.Errors>
{
  abstract execute(
    request: SubmitSurveyRPC.Request
  ): Promise<LeoRPCResult<SubmitSurveyRPC.Response, SubmitSurveyRPC.Errors.Errors>>;
}

export namespace SubmitSurveyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyLinkId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isSubmitSurveyRPCRequest: boolean = true;
  
    readonly surveyLinkId: LeoUUID;
  
  
    constructor(
    surveyLinkId: LeoUUID
    ) {
      this.surveyLinkId = surveyLinkId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyLinkId = getLeoUuid(dto, "surveyLinkId", InvalidRequestSurveyLinkIdError);
      return new Request(
        surveyLinkId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyLinkId: this.surveyLinkId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `SubmitSurveyRPC(surveyLinkId='${this.surveyLinkId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    thankYouPageText: SurveyLocalizedTextDTO;
    progressPercentage: ProgressPercentageDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isSubmitSurveyResponse: boolean = true;
  
    readonly thankYouPageText: SurveyLocalizedText;
    readonly progressPercentage: ProgressPercentage;
  
  
    constructor(
    thankYouPageText: SurveyLocalizedText, 
    progressPercentage: ProgressPercentage
    ) {
      this.thankYouPageText = thankYouPageText;
      this.progressPercentage = progressPercentage;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidResponseThankYouPageTextError));
      const progressPercentage = ProgressPercentage.fromDTO(getObject(dto, "progressPercentage", InvalidResponseProgressPercentageError));
      return new Response(
        thankYouPageText,
        progressPercentage
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          thankYouPageText: this.thankYouPageText.toDTO(),
          progressPercentage: this.progressPercentage.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `SubmitSurvey(thankYouPageText='${this.thankYouPageText}',progressPercentage='${this.progressPercentage}')`;
  };
  

  export enum RPCError {
    InvalidSurveyLinkId = "INVALID_SURVEY_LINK_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED"
  }

  export namespace Errors {
    
    export interface InvalidSurveyLinkIdDTO {
      code: string;
    }
    
    export class InvalidSurveyLinkId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyLinkId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyLinkId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyLinkIdDTO {
        const dto: InvalidSurveyLinkIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyLinkId.prototype.toString = function toString(): string {
      return `SubmitSurveyRPC.INVALID_SURVEY_LINK_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
      surveyIsClosedTitleText: SurveyLocalizedTextDTO;
      surveyIsClosedDescriptionText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
      readonly surveyIsClosedTitleText: SurveyLocalizedText;
      readonly surveyIsClosedDescriptionText: SurveyLocalizedText;
    
      constructor(
      surveyIsClosedTitleText: SurveyLocalizedText, 
      surveyIsClosedDescriptionText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
        this.surveyIsClosedTitleText = surveyIsClosedTitleText;
        this.surveyIsClosedDescriptionText = surveyIsClosedDescriptionText;
      }
    
      static fromDTO(dto: SurveyIsClosedDTO): SurveyIsClosed {
        const surveyIsClosedTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedTitleText", InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError));
        const surveyIsClosedDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedDescriptionText", InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError));
        return new SurveyIsClosed(
          surveyIsClosedTitleText,
          surveyIsClosedDescriptionText
        );
      }
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
          surveyIsClosedTitleText: this.surveyIsClosedTitleText.toDTO(),
          surveyIsClosedDescriptionText: this.surveyIsClosedDescriptionText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `SubmitSurveyRPC.SURVEY_IS_CLOSED(surveyIsClosedTitleText='${this.surveyIsClosedTitleText}',surveyIsClosedDescriptionText='${this.surveyIsClosedDescriptionText}')`;
    };
    

    export type Errors = InvalidSurveyLinkId | SurveyIsClosed;
    export type ErrorsDTO = InvalidSurveyLinkIdDTO | SurveyIsClosedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyLinkId:
          return new InvalidSurveyLinkId();
        case RPCError.SurveyIsClosed:
          return SurveyIsClosed.fromDTO(dto as SurveyIsClosedDTO);
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyLinkIdError extends SubmitSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyLinkIdError";
  }
}

export class InvalidResponseThankYouPageTextError extends SubmitSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseThankYouPageTextError";
  }
}

export class InvalidResponseProgressPercentageError extends SubmitSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProgressPercentageError";
  }
}

export class InvalidResponseInvalidSurveyLinkIdError extends SubmitSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyLinkIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends SubmitSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError";
  }
}
